import React from 'react'
import { useLocation } from 'react-router-dom'
import { Box, Typography, Paper, Alert, CircularProgress } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { telasApi } from '../api/telas'
import { GenericEditableGrid } from '../components/Basicas/GenericEditableGrid'

/**
 * Página genérica para renderizar telas dinâmicas
 * 
 * Por enquanto, apenas exibe informações da tela.
 * Futuramente, pode ser expandido para renderizar formulários,
 * tabelas, ou outros componentes baseados na configuração da tela.
 */
const DynamicTelaPage: React.FC = () => {
  const location = useLocation()
  const rotaCompleta = location.pathname // Ex: "/vendas" ou "/estoque/produtos"

  // Buscar tela pela rota
  const { data: tela, isLoading, error } = useQuery({
    queryKey: ['tela', 'rota', rotaCompleta],
    queryFn: async () => {
      const todasTelas = await telasApi.list({ tipo_sistema: 'GALATEA_WEB', ativo: true })
      // Buscar tela que corresponde à rota atual
      return todasTelas.find((t) => {
        const telaRota = t.rota.startsWith('/') ? t.rota : `/${t.rota}`
        return telaRota === rotaCompleta
      })
    },
    enabled: !!rotaCompleta,
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  })

  if (isLoading) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Erro ao carregar tela: {error instanceof Error ? error.message : 'Erro desconhecido'}
        </Alert>
      </Box>
    )
  }

  if (!tela) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          Tela não encontrada para a rota: {rotaCompleta}
          <br />
          Verifique se a tela está cadastrada no sistema e você tem permissão para acessá-la.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%' }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {tela.nome}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
          {tela.descricao}
        </Typography>
      </Paper>

      {tela.layout_tipo === 'CARD' && tela.slug_entidade ? (
        <GenericEditableGrid
          telaId={tela.id}
          slug={tela.slug_entidade}
          title={tela.nome}
          accentColor={tela.card_color || undefined}
        />
      ) : (
        <Alert severity="info">
          Esta é uma página dinâmica. A funcionalidade completa será implementada conforme necessário.
          <br />
          <strong>Rota:</strong> {tela.rota}
          <br />
          <strong>Tipo Sistema:</strong> {tela.tipo_sistema}
        </Alert>
      )}
    </Box>
  )
}

export default DynamicTelaPage

